/**
 * Large-import coercion review.
 *
 * The large-import path omits inline `analyzed_rows` from `analysis_result`
 * (see `CsvImport.analyzed_rows_omitted`), so invalid rows must be fetched
 * per file via the paginated `/rows` endpoint — which recovers `file_id`
 * (the endpoint output itself omits it) by tagging each result with the
 * `fileId` it was requested for. Once fetched, renders the existing
 * `CoercionReview` with those rows injected via `injectedRows`.
 */

import { useQueries } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { type CsvImport, getCsvImportRows } from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';

import { CoercionReview, type InvalidRowItem } from './CoercionReview';
import { MAX_COERCION_ROWS } from './constants';

const STALE_TIME_MS = 30_000;

interface LargeImportCoercionReviewProps {
  importData: CsvImport;
  orgId: number;
  importId: number;
  onBack: () => void;
  onApplied: (updated: CsvImport) => void;
}

export function LargeImportCoercionReview({
  importData,
  orgId,
  importId,
  onBack,
  onApplied,
}: LargeImportCoercionReviewProps) {
  // The exact `file_id` strings the analyzer used — `file_summaries` is keyed
  // by `file_id_str`, matching `CsvAnalyzedRow.file_id`.
  const fileIds = useMemo(
    () => Object.keys(importData.analysis_result?.file_summaries ?? {}),
    [importData.analysis_result]
  );

  // One bounded request per file — the endpoint's max_limit (1000) exceeds
  // MAX_COERCION_ROWS, so a single request per file always suffices; no
  // paging loop.
  const queries = useQueries({
    queries: fileIds.map(fileId => ({
      queryKey: ['csvImportInvalidRows', orgId, importId, fileId] as const,
      queryFn: () =>
        getCsvImportRows(orgId, importId, {
          action: 'invalid',
          fileId,
          limit: MAX_COERCION_ROWS,
          offset: 0,
        }),
      staleTime: STALE_TIME_MS,
    })),
  });

  const isLoading = queries.some(q => q.isLoading);
  const isError = queries.some(q => q.isError);

  useEffect(() => {
    if (!isError) {
      return;
    }
    logger.error('Failed to load invalid rows for large import', undefined, {
      import_id: importId,
      org_id: orgId,
    });
  }, [isError, importId, orgId]);

  const refetchAll = useCallback(() => {
    for (const query of queries) {
      void query.refetch();
    }
  }, [queries]);

  // Aggregate memory ceiling: up to `fileIds.length * MAX_COERCION_ROWS`
  // rows held in memory at once (bounded per file, not per import).
  const injectedRows = useMemo<InvalidRowItem[]>(
    () =>
      fileIds.flatMap((fileId, i) =>
        (queries[i].data?.results ?? []).map(row => ({ fileId, row }))
      ),
    [fileIds, queries]
  );

  const truncated = useMemo(
    () =>
      fileIds.some((_, i) => (queries[i].data?.count ?? 0) > MAX_COERCION_ROWS),
    [fileIds, queries]
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <LoadingIndicator size='sm' label='Loading rows that need fixes…' />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        role='alert'
        className='flex flex-col items-center gap-3 py-12 text-center'
      >
        <p className='text-sm text-fg-secondary'>
          We couldn&apos;t load the rows that need fixes.
        </p>
        <Button color='secondary' size='sm' onClick={refetchAll}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {truncated && (
        <div className='rounded-lg border border-border-secondary bg-bg-secondary px-3 py-2 text-sm text-fg-secondary'>
          Showing the first {MAX_COERCION_ROWS} rows that need fixes in each
          file. Any remaining invalid rows will be skipped on import.
        </div>
      )}
      <CoercionReview
        importData={importData}
        orgId={orgId}
        importId={importId}
        onBack={onBack}
        onApplied={onApplied}
        injectedRows={injectedRows}
      />
    </div>
  );
}
